import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { LawFirm } from '@models/lawFirms';
import {
	ActingForType,
	BusinessMatterClientWithAssociations,
	MatterClientType,
	MatterFileType,
	PersonMatterClientWithAssociations,
} from '@models/matter';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { select, Select } from '@ngxs/store';
import { BusinessFeatureFlagSelectors, BusinessState } from '@state/business';
import { MatterClients } from '@state/global/matter_clients';
import { MatterDispatch } from '@state/matter';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { IdvRequestModalComponent } from '../idv-modal/idv-modal.component';
import { ClientIntakeService } from 'src/app/shared/services/client-intake/client-intake.service';

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	selector: 'app-parties-mortgagors',
	templateUrl: './mortgagors.component.html',
	styleUrls: ['./mortgagors.component.scss'],
	standalone: false,
})
export class PartiesMortgagorsComponent implements OnInit {
	@SelectSnapshot(SelectedMatter.id) matterId: string;

	@Select(SelectedMatter.purchasersResideAtProperty)
	purchaserResideInProperty$!: Observable<boolean>;
	@Select(SelectedMatter.matterFileType)
	matterFileType$: Observable<MatterFileType>;
	@Select(SelectedMatter.actingFor) actingFor$!: Observable<ActingForType>;
	@Select(SelectedMatter.otherPartyLawFirm)
	mortgagorLawFirm$!: Observable<LawFirm>; // this is being updated and not calling down change detection
	@Select(SelectedMatter.otherPartyFileNumber)
	otherPartyFileNumber$!: Observable<string>;
	@Select(MatterClients.get_matterClientsWithAssociationMapped)
	matterClients$!: Observable<
		(
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[]
	>;
	@SelectSnapshot(BusinessState.getTreefortConnected)
	isTreefortConnected: Observable<boolean>;
	isClientIntakeEnabled_ = select(
		BusinessFeatureFlagSelectors.isClientIntakeEnabled,
	);

	public clientTypes = MatterClientType;
	public intakeButtonText: string;

	showActingFor$!: Observable<boolean>;
	showDetails$!: Observable<boolean>;
	showLawFirms$!: Observable<boolean>;

	@SelectSnapshot(SelectedMatter.isLeapMatter) isLeapMatter: boolean;

	constructor(
		private matterDispatch: MatterDispatch,
		private matDialog: MatDialog,
		private clientIntakeService: ClientIntakeService,
	) {}

	ngOnInit(): void {
		this.intakeButtonText = this.clientIntakeService.getIntakeButtonText();

		this.showActingFor$ = this.matterFileType$.pipe(
			map((type) => type === MatterFileType.REFINANCE),
		);

		this.showDetails$ = this.matterFileType$.pipe(
			map(
				(type) =>
					type === MatterFileType.PURCHASE || type === MatterFileType.REFINANCE,
			),
		);

		this.showLawFirms$ = this.actingFor$.pipe(
			map((actingFor) => actingFor === ActingForType.MORTGAGEE),
		);
	}

	setPurchasersResiding(purchaserResiding: boolean): void {
		this.matterDispatch.SetPurchaserResidingInProperty(
			this.matterId,
			purchaserResiding,
		);
	}

	trackClient(
		index: number,
		item:
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations,
	): string {
		return item.id;
	}

	onActingForChange(actingFor: ActingForType): void {
		this.matterDispatch.SetActingFor(this.matterId, actingFor);
	}

	onMortgagorsLawFirmUpdate(value: LawFirm): void {
		// if (this.isLeapMatter) {
		// 	// We are doing this differently for LEAP because this component does not follow the same flow (for reasons I don't understand) as law-firm-side-form.component.
		// 	// We need to change this component to follow the same flow as law-firm-side-form.component
		// 	if (!value) {
		// 		this.matterDispatch.RemoveLawFirm(this.matterId);
		// 	} else {
		// 		if ((value as any).selectLawyer) {
		// 			delete (value as any).selectLawyer;
		// 			this.matterDispatch.SelectLawFirmEmployee(
		// 				this.matterId,
		// 				value.id,
		// 				(value as any).lawyer.id,
		// 				LawFirmEmployeeType.LAWYER,
		// 				value.leap.personId,
		// 			);
		// 		} else if ((value as any).selectClerk) {
		// 			delete (value as any).selectClerk;
		// 			this.matterDispatch.SelectLawFirmEmployee(
		// 				this.matterId,
		// 				value.id,
		// 				(value as any).clerk.id,
		// 				LawFirmEmployeeType.CLERK,
		// 				(value as any).clerk.leap.personId,
		// 			);
		// 		} else if ((value as any).removeLawyer) {
		// 			delete (value as any).removeLawyer;
		// 			this.matterDispatch.RemoveLawFirmEmployee(
		// 				this.matterId,
		// 				LawFirmEmployeeType.LAWYER,
		// 			);
		// 		} else if ((value as any).removeClerk) {
		// 			delete (value as any).removeClerk;
		// 			this.matterDispatch.RemoveLawFirmEmployee(
		// 				this.matterId,
		// 				LawFirmEmployeeType.CLERK,
		// 			);
		// 		} else if (
		// 			(value as any).createLawFirm ||
		// 			(value as any).updateLawFirm
		// 		) {
		// 			delete (value as any).createLawFirm;
		// 			delete (value as any).updateLawFirm;
		// 			this.matterDispatch.CreateLawFirm(this.matterId, value);
		// 		} else if ((value as any).createOrUpdateLawyer) {
		// 			delete (value as any).createOrUpdateLawyer;
		// 			this.matterDispatch.CreateLawFirmEmployee(
		// 				this.matterId,
		// 				(value as any).lawyer,
		// 				value.id,
		// 			);
		// 		} else if ((value as any).createOrUpdateClerk) {
		// 			delete (value as any).createOrUpdateClerk;
		// 			this.matterDispatch.CreateLawFirmEmployee(
		// 				this.matterId,
		// 				(value as any).clerk,
		// 				value.id,
		// 			);
		// 		} else {
		// 			this.matterDispatch.SelectLawFirm(
		// 				this.matterId,
		// 				value.id,
		// 				value.leap,
		// 			);
		// 		}
		//
		// 		this.deleteExtraProperties(value);
		// 	}
		// } else {
		if (value) {
			this.deleteExtraProperties(value);
		}
		this.matterDispatch.Update(this.matterId, { otherPartyLawFirm: value });
		//}
	}

	deleteExtraProperties(value: any) {
		delete (value as any).selectLawyer;
		delete (value as any).removeLawyer;
		delete (value as any).selectClerk;
		delete (value as any).removeClerk;
		delete (value as any).createLawFirm;
		delete (value as any).updateLawFirm;
		delete (value as any).createOrUpdateLawyer;
		delete (value as any).createOrUpdateClerk;
	}

	onFileNumberUpdated(otherPartyFileNumber: string): void {
		this.matterDispatch.Update(this.matterId, { otherPartyFileNumber });
	}

	verify(): void {
		this.openIdvRequestModal();
	}

	sendOrViewIntake() {
		this.clientIntakeService.sendOrViewIntake(this.matterId);
	}

	private openIdvRequestModal(): void {
		this.matDialog.open(IdvRequestModalComponent, {
			data: { hasCheckboxes: true },
			width: '80vw',
			maxWidth: '1000px',
		});
	}
}
